/**
 * Configuración de internacionalización (i18n)
 *
 * Estructura de namespaces:
 * ┌─────────────────────────────────────────────────────────────┐
 * │  common     → Textos compartidos (navegación, acciones)    │
 * │  auth       → Login, registro, MFA, permisos               │
 * │  dashboard  → Página principal, saldo, CLABE               │
 * │  transfers  → Transferencias, beneficiarios                │
 * │  wallet     → Billetera, movimientos, saldo                │
 * │  rotatingSavings → Rotating savings (tandas), contributions │
 * │  investments → Investments, portfolio                       │
 * │  credits     → Credits, credit limit                        │
 * │  savingsBox  → Savings boxes, amounts                       │
 * │  contacts    → Contacts, beneficiaries                      │
 * │  payments    → Payment orders and SPEI status               │
 * │  notifications → Notifications, preferences                │
 * │  settings   → Configuración de usuario                     │
 * │  errors     → Mensajes de error                            │
 * │  products   → Catálogo, elegibilidad, tarjetas de producto │
 * └─────────────────────────────────────────────────────────────┘
 *
 * Uso en componentes:
 *
 *   import { useTranslation } from '@tankef/i18n';
 *
 *   function MyComponent() {
 *     const { t } = useTranslation('transfers');
 *     return <h1>{t('title')}</h1>;  // "Transferencias"
 *   }
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Importar traducciones - Español
import esCommon from '../locales/es/common.json';
import esAuth from '../locales/es/auth.json';
import esDashboard from '../locales/es/dashboard.json';
import esTransfers from '../locales/es/transfers.json';
import esErrors from '../locales/es/errors.json';
import esWallet from '../locales/es/wallet.json';
import esRotatingSavings from '../locales/es/rotatingSavings.json';
import esInvestments from '../locales/es/investments.json';
import esCredits from '../locales/es/credits.json';
import esSavingsBox from '../locales/es/savingsBox.json';
import esContacts from '../locales/es/contacts.json';
import esTransfer from '../locales/es/transfer.json';
import esPayments from '../locales/es/payments.json';
import esNotifications from '../locales/es/notifications.json';
import esSettings from '../locales/es/settings.json';
import esOnboarding from '../locales/es/onboarding.json';
import esOnboardingMoral from '../locales/es/onboarding-moral.json';
import esProducts from '../locales/es/products.json';

// Importar traducciones - Inglés
import enCommon from '../locales/en/common.json';
import enAuth from '../locales/en/auth.json';
import enDashboard from '../locales/en/dashboard.json';
import enTransfers from '../locales/en/transfers.json';
import enErrors from '../locales/en/errors.json';
import enWallet from '../locales/en/wallet.json';
import enRotatingSavings from '../locales/en/rotatingSavings.json';
import enInvestments from '../locales/en/investments.json';
import enCredits from '../locales/en/credits.json';
import enSavingsBox from '../locales/en/savingsBox.json';
import enContacts from '../locales/en/contacts.json';
import enTransfer from '../locales/en/transfer.json';
import enPayments from '../locales/en/payments.json';
import enNotifications from '../locales/en/notifications.json';
import enSettings from '../locales/en/settings.json';
import enOnboarding from '../locales/en/onboarding.json';
import enProducts from '../locales/en/products.json';

export const resources = {
  es: {
    common: esCommon,
    auth: esAuth,
    dashboard: esDashboard,
    transfers: esTransfers,
    errors: esErrors,
    wallet: esWallet,
    rotatingSavings: esRotatingSavings,
    investments: esInvestments,
    credits: esCredits,
    savingsBox: esSavingsBox,
    contacts: esContacts,
    transfer: esTransfer,
    payments: esPayments,
    notifications: esNotifications,
    settings: esSettings,
    onboarding: esOnboarding,
    onboardingMoral: esOnboardingMoral,
    products: esProducts,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    dashboard: enDashboard,
    transfers: enTransfers,
    errors: enErrors,
    wallet: enWallet,
    rotatingSavings: enRotatingSavings,
    investments: enInvestments,
    credits: enCredits,
    savingsBox: enSavingsBox,
    contacts: enContacts,
    transfer: enTransfer,
    payments: enPayments,
    notifications: enNotifications,
    settings: enSettings,
    onboarding: enOnboarding,
    products: enProducts,
  },
};

// Idiomas soportados
export const supportedLanguages = ['es', 'en'] as const;
export type Language = (typeof supportedLanguages)[number];

// Configurar i18next
i18n
  // Detectar idioma del navegador
  .use(LanguageDetector)
  // Integración con React
  .use(initReactI18next)
  .init({
    resources,
    // Namespace por defecto
    defaultNS: 'common',
    // Idioma por defecto si no se detecta
    fallbackLng: 'es',
    // Solo idiomas que soportamos
    supportedLngs: supportedLanguages,
    // No escapar HTML (React ya lo hace)
    interpolation: {
      escapeValue: false,
    },
    // Configuración del detector
    detection: {
      // Orden de prioridad para detectar idioma
      order: ['localStorage', 'navigator'],
      // Key en localStorage
      lookupLocalStorage: 'tankef-language',
    },
  });

export default i18n;

// Re-exportar hooks para uso fácil
export { useTranslation, Trans } from 'react-i18next';

// Helper para cambiar idioma
export function changeLanguage(lang: Language) {
  return i18n.changeLanguage(lang);
}

// Helper para obtener idioma actual
export function getCurrentLanguage(): Language {
  return i18n.language as Language;
}
